import Cookies from 'js-cookie';
import cookieDomain from '../../../shared/utils/cookieDomain';

import {
  CANCEL_REGISTRATION,
  CHECK_IDNTY_GOOGLE_ACCOUNT_START,
  CHECK_IDNTY_GOOGLE_ACCOUNT_FULFILLED,
  CHECK_IDNTY_GOOGLE_ACCOUNT_REJECTED,
  CLEAR_EASY_JOIN_ERROR,
  CLEAR_GOOGLE_ERROR,
  REGISTER_EXISTING_USER_START,
  REGISTER_EXISTING_USER_FULFILLED,
  REGISTER_EXISTING_USER_REJECTED,
  REGISTER_USER_START,
  REGISTER_USER_FULFILLED,
  REGISTER_USER_REJECTED,
  RESET_EASY_JOIN,
  SET_GOOGLE_USER,
  SET_REGISTERING_WITH_GOOGLE,
  SET_SUBMITTING,
  SUBMIT_EASY_JOIN_CODE_START,
  SUBMIT_EASY_JOIN_CODE_FULFILLED,
  SUBMIT_EASY_JOIN_CODE_REJECTED,
} from './actionTypes';

import { axiosLogin, axiosRegister } from '../../../shared/utils/setCommonHeaders';
import decodeGoogleIdToken from '../../../shared/utils/decodeGoogleIdToken';
import encode from '../../../shared/utils/encodeToUrl';
import { showErrorToast } from '../../../shared/utils/toastManager';
import {
  authProvider, fetchLogin, saveAuthTokens,
} from '../../login/ducks/loginActions';

authProvider();

export const clearEasyJoinError = () => (
  {
    type: CLEAR_EASY_JOIN_ERROR,
  }
);

// Cancel registration, clear cookies (logout), and redirect to login page
export const cancelRegistration = (clearCookies) => (dispatch) => {
  dispatch({ type: CANCEL_REGISTRATION });
  // Clear SSO registration flag when cancelling
  sessionStorage.removeItem('isRegisteringWithSSO');
  if (clearCookies) {
    Cookies.remove('v2token', {
      secure: process.env.ENVIRONMENT !== 'LOCAL' && true,
      domain: cookieDomain,
    });
    Cookies.remove('accessToken', {
      secure: process.env.ENVIRONMENT !== 'LOCAL' && true,
      domain: cookieDomain,
    });
    Cookies.remove('refreshToken', {
      secure: process.env.ENVIRONMENT !== 'LOCAL' && true,
      domain: cookieDomain,
    });
    Cookies.remove('tokenExpiration', {
      secure: process.env.ENVIRONMENT !== 'LOCAL' && true,
      domain: cookieDomain,
    });
  }
  window.location.href = '/login';
};

export const registerUser = (values, isIdnty = false) => (dispatch) => {
  dispatch({ type: REGISTER_USER_START });
  axiosRegister.post('/register', values)
    .then((response) => {
      const { data } = response.data;
      // Clear SSO registration flag on successful registration
      sessionStorage.removeItem('isRegisteringWithSSO');
      setTimeout(() => {
        dispatch({
          payload: { data },
          type: REGISTER_USER_FULFILLED,
        });
      }, 800);
      // For IDNTY registration, skip fetchLogin to prevent its window.location redirect
      // from firing before RegistrationContainer can navigate to /idnty/success.
      // The isComplete flag + RegistrationContainer's useEffect handles the redirect instead.
      if (!isIdnty) {
        saveAuthTokens(
          data.v2_token,
          data.accessToken,
          data.refreshToken,
          data.expires_in,
        );
        dispatch(fetchLogin());
      }
    })
    .catch((err) => {
      dispatch({
        payload: err,
        type: REGISTER_USER_REJECTED,
      });
      showErrorToast(err?.response?.headers?.message || 'Something went wrong, please try again.');
    });
};

export const registerUserSharedWorkout = (values) => (dispatch) => {
  dispatch({ type: REGISTER_USER_START });
  axiosRegister.post('/public/account', values)
    .then((response) => {
      const { cookies } = response.data;
      setTimeout(() => {
        dispatch({
          type: REGISTER_USER_FULFILLED,
        });
      }, 800);
      saveAuthTokens(
        cookies.v2_token,
        cookies.accessToken,
        cookies.refreshToken,
        cookies.refreshTokenExpires,
      );
      dispatch(fetchLogin());
    })
    .catch((err) => {
      dispatch({
        payload: err,
        type: REGISTER_USER_REJECTED,
      });
      showErrorToast(err?.response?.headers?.message || 'Something went wrong, please try again.');
    });
};

export const resetEasyJoin = () => ({
  type: RESET_EASY_JOIN,
});

export const setGoogleError = (bool) => {
  if (bool) {
    showErrorToast('There was an error connecting with Google, please try again.');
  }
  return {
    type: CLEAR_GOOGLE_ERROR,
  };
};

export const setGoogleUser = (isRegistering) => ({
  payload: isRegistering,
  type: SET_GOOGLE_USER,
});

export const setRegisteringWithGoogle = (isRegistering) => ({
  payload: isRegistering,
  type: SET_REGISTERING_WITH_GOOGLE,
});

export const setSubmitting = (isSubmitting) => ({
  payload: isSubmitting,
  type: SET_SUBMITTING,
});

export const submitEasyJoinCode = (values) => (dispatch) => {
  dispatch({ type: SUBMIT_EASY_JOIN_CODE_START });
  axiosLogin
    .get(
      `/easy-joins?easyJoinCode=${values.easyJoinCode}&easyJoinPassword=${values.easyJoinPassword}`,
    )
    .then(async (response) => {
      if (response.data && response.data.length) {
        setTimeout(() => {
          dispatch({
            payload: response.data[0],
            type: SUBMIT_EASY_JOIN_CODE_FULFILLED,
          });
        }, 1000);
      } else {
        dispatch({
          payload:
            'The submitted Easy-Join code or password is invalid. Please try again.',
          type: SUBMIT_EASY_JOIN_CODE_REJECTED,
        });
      }
    })
    .catch((err) => {
      dispatch({
        payload: err,
        type: SUBMIT_EASY_JOIN_CODE_REJECTED,
      });
    });
};

// Below: actions for an existing (non-IDNTY) TeamBuildr account joining
// IDNTY - as opposed to registerUser's isIdnty branch above, which is for a
// brand-new account signing up directly into IDNTY.

export const IDNTY_GOOGLE_STATUS = {
  ALREADY_MEMBER: 'ALREADY_MEMBER',
  JOINED: 'JOINED',
  NEW_USER: 'NEW_USER',
  PENDING_JOIN: 'PENDING_JOIN',
  ERROR: 'ERROR',
};

// Wraps decodeGoogleIdToken, normalizing a failed decode to empty fields
// (not null) since every caller here spreads/reads off the result directly.
const decodeGoogleToken = (idToken) => decodeGoogleIdToken(idToken) || {
  email: undefined, familyName: undefined, givenName: undefined,
};

// No stable "already joined" error code exists yet, so this matches the
// known message text (mirrors isEmailInUseError). The 400 fires whenever the
// account already has an IDNTY mult_acct row, independent of team_users.code.
const isAlreadyJoinedError = (err) => {
  const message = err?.response?.headers?.message || '';
  return message.toLowerCase().includes('already joined');
};

// Joins an existing TeamBuildr account to IDNTY; the credential is verified
// server-side, same as /register. Never persists the response's oauth
// tokens - joining IDNTY must never start a real session. Always resolves
// (never rejects) with { success, alreadyJoined } instead of throwing, and
// suppresses the toast for "already joined", since callers treat that as a
// benign redirect, not a failure.
export const registerExistingUser = ({
  authenticationMethod, easyJoinId, email, googleToken, password, programId,
}) => (dispatch) => {
  dispatch({ type: REGISTER_EXISTING_USER_START });
  return axiosRegister.put('/idnty/register-existing-user', {
    authenticationMethod,
    // Env vars and URLSearchParams.get() are always strings; the endpoint's
    // schema requires integers, so coerce once here rather than per call site.
    easyJoinId: Number(easyJoinId),
    ...(authenticationMethod === 'PASSWORD' && { email, password }),
    ...(authenticationMethod === 'GOOGLE' && { googleToken }),
    ...(programId && { programId: Number(programId) }),
  })
    .then(() => {
      dispatch({ type: REGISTER_EXISTING_USER_FULFILLED });
      return { success: true };
    })
    .catch((err) => {
      dispatch({
        payload: err,
        type: REGISTER_EXISTING_USER_REJECTED,
      });
      const alreadyJoined = isAlreadyJoinedError(err);
      if (!alreadyJoined) {
        showErrorToast(err?.response?.headers?.message || 'Something went wrong, please try again.');
      }
      return { alreadyJoined, success: false };
    });
};

export const resolveJoinResult = (result) => {
  if (result?.alreadyJoined) {
    return { status: IDNTY_GOOGLE_STATUS.ALREADY_MEMBER };
  }
  if (!result?.success) {
    return { status: IDNTY_GOOGLE_STATUS.ERROR };
  }
  return { status: IDNTY_GOOGLE_STATUS.JOINED };
};

// Resolves a Google sign-in to one of four outcomes without committing a
// join: verifies identity via the same grant normal sign-in uses (never
// persisted as a session), then diffs GET /me/accounts against
// IDNTY_ACCT_CODE to decide ALREADY_MEMBER vs. PENDING_JOIN. PENDING_JOIN
// always waits on explicit confirmation before registerExistingUser runs -
// a Google account picker is a single, easy-to-mis-click action. A 400 (no
// account, or a bad/expired token) resolves NEW_USER, mirroring
// SharedLoginForm.js; anything else (no response, or a 5xx) is a real
// failure and gets surfaced instead.
export const checkIdntyGoogleAccount = (idToken) => (dispatch) => {
  dispatch({ type: CHECK_IDNTY_GOOGLE_ACCOUNT_START });
  return axiosLogin.post('/oauth/token', encode({
    client_id: process.env.CLIENT_ID,
    client_secret: process.env.CLIENT_SECRET,
    grant_type: 'google',
    scope: 'offline_access',
    tokenid: idToken,
  }))
    .then((response) => {
      // A matched account with no org membership (team_users.code === 0)
      // returns showJoinAthleteCommunity: true with no usable access_token,
      // so /me/accounts would 401 - and code === 0 doesn't rule out already
      // being in IDNTY either (mult_acct is tracked separately). Only
      // actually attempting the join can tell ALREADY_MEMBER from JOINED,
      // so PENDING_JOIN covers both here.
      if (response.data.showJoinAthleteCommunity) {
        dispatch({ type: CHECK_IDNTY_GOOGLE_ACCOUNT_FULFILLED });
        return { status: IDNTY_GOOGLE_STATUS.PENDING_JOIN, decoded: decodeGoogleToken(idToken) };
      }

      const { access_token: accessToken } = response.data;
      return axiosRegister.get('/me/accounts', {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then((accountsResponse) => {
          dispatch({ type: CHECK_IDNTY_GOOGLE_ACCOUNT_FULFILLED });
          const isAlreadyMember = (accountsResponse.data || []).some(
            (account) => String(account.id) === String(process.env.IDNTY_ACCT_CODE),
          );
          if (isAlreadyMember) {
            return { status: IDNTY_GOOGLE_STATUS.ALREADY_MEMBER };
          }
          return { status: IDNTY_GOOGLE_STATUS.PENDING_JOIN, decoded: decodeGoogleToken(idToken) };
        })
        .catch((err) => {
          dispatch({
            payload: err,
            type: CHECK_IDNTY_GOOGLE_ACCOUNT_REJECTED,
          });
          showErrorToast(err?.response?.headers?.message || 'Something went wrong, please try again.');
          return { status: IDNTY_GOOGLE_STATUS.ERROR };
        });
    })
    .catch((err) => {
      if (!err?.response || err.response.status >= 500) {
        dispatch({
          payload: err,
          type: CHECK_IDNTY_GOOGLE_ACCOUNT_REJECTED,
        });
        showErrorToast('There was an error connecting with Google, please try again.');
        return { status: IDNTY_GOOGLE_STATUS.ERROR };
      }
      // No matching account - the expected signal for a brand-new user, not
      // an error. Decode locally (same as GoogleAuthSection.js) to pre-fill
      // the new-user form instead of making them retype name/email.
      dispatch({ type: CHECK_IDNTY_GOOGLE_ACCOUNT_FULFILLED });
      return { status: IDNTY_GOOGLE_STATUS.NEW_USER, decoded: decodeGoogleToken(idToken) };
    });
};
